import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { KeyRound } from "lucide-react";
import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { PerfilAcesso, TipoUsuario } from "../interface/Usuario.interface";
import { CriarUsuarioFormData } from "../schemas/usuario.schema";

interface UsuarioProps {
  form: UseFormReturn<CriarUsuarioFormData>;
  formData: CriarUsuarioFormData;
  perfisAcesso: PerfilAcesso[];
  tiposUsuario: TipoUsuario[];
  usuario?: any;
  readOnly?: boolean;
  onTipoUsuarioChange?: (tipoUsuarioId: string) => void;
}

export const Usuario = ({ form, formData, perfisAcesso, tiposUsuario, usuario, readOnly = false, onTipoUsuarioChange }: UsuarioProps) => {
  const [usernameError, setUsernameError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [verificandoUsername, setVerificandoUsername] = useState(false);
  const [verificandoEmail, setVerificandoEmail] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  
  const isModoEdicao = !!usuario?.id;

  // Reseta o estado mostrarSenha quando o usuário mudar ou quando não estiver editando
  useEffect(() => {
    setMostrarSenha(false);
  }, [usuario?.id]);

  const verificarUsername = async (username: string) => {
    if (!username || username.trim() === "") {
      setUsernameError("");
      return;
    }

    // Se estiver editando e o username não mudou, não precisa verificar
    if (usuario?.id && usuario.username === username.trim()) {
      setUsernameError("");
      return;
    }

    setVerificandoUsername(true);
    setUsernameError("");

    try {
      const url = usuario?.id 
        ? `/api/usuario/verificar-username?excludeId=${usuario.id}`
        : `/api/usuario/verificar-username`;
      const response = await api.post(url, {
        username: username.trim()
      });

      if (!response.result?.disponivel) {
        setUsernameError("Este username já está em uso");
      }
    } catch (error: any) {
      console.error("Erro ao verificar username:", error);
      // Não mostra erro no toast para não poluir a interface
      // Apenas define o erro no campo
      setUsernameError("Erro ao verificar disponibilidade");
    } finally {
      setVerificandoUsername(false);
    }
  };

  const verificarEmail = async (email: string) => {
    if (!email || email.trim() === "") {
      setEmailError("");
      return;
    }

    // Validação básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Email inválido");
      return;
    }

    // Se estiver editando e o email não mudou, não precisa verificar
    if (usuario?.id && usuario.email === email.trim()) {
      setEmailError("");
      return;
    }

    setVerificandoEmail(true);
    setEmailError("");

    try {
      const url = usuario?.id 
        ? `/api/usuario/verificar-email?excludeId=${usuario.id}`
        : `/api/usuario/verificar-email`;
      const response = await api.post(url, {
        email: email.trim()
      });

      if (!response.result?.disponivel) {
        setEmailError("Este email já está em uso");
      }
    } catch (error: any) {
      console.error("Erro ao verificar email:", error);
      setEmailError("Erro ao verificar disponibilidade");
    } finally {
      setVerificandoEmail(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Dados do Usuário</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                E-mail <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder="exemplo@email.com"
                  className={emailError ? "border-red-500" : ""}
                  disabled={verificandoEmail || readOnly}
                  readOnly={readOnly}
                  onBlur={(e) => {
                    field.onBlur();
                    verificarEmail(e.target.value);
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    setEmailError("");
                  }}
                />
              </FormControl>
              {emailError && (
                <p className="text-sm text-red-500">{emailError}</p>
              )}
              {verificandoEmail && (
                <p className="text-sm text-muted-foreground">Verificando disponibilidade...</p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="username"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Username <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value || ""}
                  placeholder="Nome de usuário"
                  className={usernameError ? "border-red-500" : ""}
                  disabled={verificandoUsername || readOnly}
                  readOnly={readOnly}
                  onBlur={(e) => {
                    field.onBlur();
                    verificarUsername(e.target.value);
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    setUsernameError("");
                  }}
                />
              </FormControl>
              {usernameError && (
                <p className="text-sm text-red-500">{usernameError}</p>
              )}
              {verificandoUsername && (
                <p className="text-sm text-muted-foreground">Verificando disponibilidade...</p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Campo de senha - só mostra se não estiver editando OU se clicou em redefinir senha */}
        {(!isModoEdicao || mostrarSenha) && (
          <FormField
            control={form.control}
            name="senha"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Senha {!isModoEdicao && <span className="text-red-500">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="password"
                    placeholder={isModoEdicao ? "Digite a nova senha (mínimo 6 caracteres)" : "Mínimo 6 caracteres"}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Botão para redefinir senha - só mostra no modo de edição */}
        {isModoEdicao && !mostrarSenha && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setMostrarSenha(true);
                // Limpa o campo de senha quando mostrar
                form.setValue("senha", "");
              }}
              className="w-full"
            >
              <KeyRound className="h-4 w-4 mr-2" />
              Redefinir Senha
            </Button>
          </div>
        )}

        {/* Botão para cancelar redefinição de senha */}
        {isModoEdicao && mostrarSenha && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setMostrarSenha(false);
                // Limpa o campo de senha quando ocultar
                form.setValue("senha", "");
              }}
              className="w-full"
            >
              Cancelar Redefinição
            </Button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="id_perfil_acesso"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Perfil de Acesso <span className="text-red-500">*</span>
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value || ""} disabled={readOnly}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o perfil" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {perfisAcesso.map((perfil) => (
                      <SelectItem key={perfil.id} value={perfil.id.toString()}>
                        {perfil.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="id_tipo_usuario"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Tipo de Usuário <span className="text-red-500">*</span>
                </FormLabel>
                <Select 
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Chama o callback quando o tipo de usuário mudar
                    if (onTipoUsuarioChange) {
                      onTipoUsuarioChange(value);
                    }
                  }} 
                  value={field.value || ""}
                  disabled={readOnly}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {tiposUsuario.map((tipo) => (
                      <SelectItem key={tipo.id} value={tipo.id.toString()}>
                        {tipo.descricao}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          /> 
        </div>
      </CardContent>
    </Card>
  );
};

